import React from 'react';
import ArchitectureLayout from 'components/architecture/ArchitectureLayout';
import {
  DatabaseOverviewDiagram,
  DataModelDiagram,
  PartitioningDiagram,
  ConsistencyDiagram,
  ChangeFeedDiagram,
  BackupDiagram
} from 'components/architecture/DatabaseDiagram';

/**
 * Database Architecture Page
 * Documents the database infrastructure
 */
const Database = () => {
  const sections = [
    {
      id: 'overview',
      title: 'Database Overview',
      content: [
        '<p>We use <strong>MongoDB</strong>, a globally distributed, multi-model database service that provides:</p>',
        '<ul>',
        '<li>Global distribution across multiple regions</li>',
        '<li>Multi-model support (document, key-value, graph)</li>',
        '<li>Automatic scaling</li>',
        '<li>Single-digit millisecond latency</li>',
        '<li>Guaranteed high availability</li>',
        '</ul>',
        <DatabaseOverviewDiagram key="overview-diagram" />
      ]
    },
    {
      id: 'data-model',
      title: 'Data Model',
      content: [
        '<p>Our database uses a <strong>document model</strong> with:</p>',
        '<ul>',
        '<li><strong>Containers</strong>: Logical grouping of documents (similar to tables)</li>',
        '<li><strong>Documents</strong>: JSON documents stored in containers</li>',
        '<li><strong>Partition keys</strong>: Enable horizontal scaling and efficient queries</li>',
        '<li><strong>Indexing</strong>: Automatic indexing on all properties</li>',
        '</ul>',
        <DataModelDiagram key="data-model-diagram" />
      ]
    },
    {
      id: 'partitioning',
      title: 'Partitioning Strategy',
      content: [
        '<p>We partition data using:</p>',
        '<ul>',
        '<li><strong>Tenant ID</strong>: Primary partition key for multi-tenant isolation</li>',
        '<li><strong>Resource ID</strong>: Secondary partitioning for large resources</li>',
        '<li><strong>Time-based</strong>: Partitioning by date for time-series data</li>',
        '</ul>',
        '<p>This ensures:</p>',
        '<ul>',
        '<li>Efficient queries within tenant boundaries</li>',
        '<li>Horizontal scaling across partitions</li>',
        '<li>Isolation between tenants</li>',
        '<li>Optimal performance for common access patterns</li>',
        '</ul>',
        <PartitioningDiagram key="partitioning-diagram" />
      ]
    },
    {
      id: 'consistency',
      title: 'Consistency & Availability',
      content: [
        '<p>We configure consistency levels based on use case:</p>',
        '<ul>',
        '<li><strong>Session Strong</strong>: For critical operations requiring immediate consistency</li>',
        '<li><strong>Session</strong>: Default for most operations (per-session consistency)</li>',
        '<li><strong>Bounded Staleness</strong>: For read-heavy workloads</li>',
        '<li><strong>Eventual</strong>: For analytics and reporting</li>',
        '</ul>',
        '<p>All data is replicated across multiple regions for high availability.</p>',
        <ConsistencyDiagram key="consistency-diagram" />
      ]
    },
    {
      id: 'change-feed',
      title: 'Change Feed',
      content: [
        '<p>We use MongoDB Change Feed for:</p>',
        '<ul>',
        '<li><strong>Real-time replication</strong>: Sync data across regions</li>',
        '<li><strong>Event processing</strong>: Trigger functions on data changes</li>',
        '<li><strong>Audit logging</strong>: Track all data modifications</li>',
        '<li><strong>Notifications</strong>: Real-time updates to connected clients</li>',
        '</ul>',
        '<p>The change feed enables reactive architectures and real-time features.</p>',
        <ChangeFeedDiagram key="change-feed-diagram" />
      ]
    },
    {
      id: 'backup',
      title: 'Backup & Recovery',
      content: [
        '<p>We maintain:</p>',
        '<ul>',
        '<li><strong>Automatic backups</strong>: Every 4 hours</li>',
        '<li><strong>Point-in-time restore</strong>: Restore to any point in the last 30 days</li>',
        '<li><strong>Geo-redundant backups</strong>: Backups stored in multiple regions</li>',
        '<li><strong>Disaster recovery</strong>: Automated failover to secondary regions</li>',
        '</ul>',
        <BackupDiagram key="backup-diagram" />
      ]
    }
  ];

  return (
    <ArchitectureLayout
      title="Database"
      lastUpdated="2025-01-15"
      sections={sections}
    />
  );
};

export default Database;

